/**
 * PromotionDialog component.
 * Multi-step modal dialog for promoting draft configurations to live.
 *
 * Steps:
 * 1. Validation — run and display validation results
 * 2. Diff preview — show draft vs live changes
 * 3. Change summary — text input for describing the change
 * 4. Confirm — promote to live
 */

import type { ReactElement } from "react";
import { useState, useCallback, useEffect } from "react";

import { DiffViewer } from "./DiffViewer";
import { ValidationPanel } from "./ValidationPanel";
import type { PromotionDialogProps } from "./types";

// ============================================================================
// Types
// ============================================================================

/** Wizard step identifiers */
type WizardStep = "validate" | "diff" | "summary" | "confirm";

/** Step configuration */
interface StepConfig {
  readonly key: WizardStep;
  readonly label: string;
  readonly number: number;
}

const STEPS: readonly StepConfig[] = [
  { key: "validate", label: "Validate", number: 1 },
  { key: "diff", label: "Review Changes", number: 2 },
  { key: "summary", label: "Change Summary", number: 3 },
  { key: "confirm", label: "Confirm", number: 4 },
];

// ============================================================================
// Component
// ============================================================================

/**
 * PromotionDialog — multi-step wizard for promoting draft configs to live.
 */
export function PromotionDialog({
  isOpen,
  onClose,
  verticalKey,
  configType,
  changedFiles,
  validationStatus,
  isValidating,
  onPromote,
  onValidate,
  mode = "direct",
  onRequestApproval,
}: PromotionDialogProps): ReactElement | null {
  const [currentStep, setCurrentStep] = useState<WizardStep>("validate");
  const [changeSummary, setChangeSummary] = useState("");
  const [isPromoting, setIsPromoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Diff data — derived from changedFiles
  const diffData = changedFiles.length > 0 ? changedFiles[0] : null;
  const draftContent = diffData
    ? JSON.stringify(
        { changes: diffData.diff.summary, fields: diffData.diff.changedFields },
        null,
        2
      )
    : "";
  const liveContent = "";

  // Check if promotion conditions are met
  const canPromote = validationStatus?.canPromote ?? false;
  const hasChanges = changedFiles.some((f) => f.diff.hasChanges);
  const hasDescription = changeSummary.trim().length >= 10;

  // Step navigation
  const canProceedFromValidate = canPromote && !isValidating;
  const canProceedFromDiff = hasChanges;
  const canProceedFromSummary = hasDescription;

  const handleRunValidation = useCallback(async (): Promise<void> => {
    setValidationError(null);
    try {
      await onValidate();
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : "Validation failed");
    }
  }, [onValidate]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep("validate");
      setChangeSummary("");
      setError(null);
      setValidationError(null);
      setIsPromoting(false);
      setApprovalSuccess(false);
    }
  }, [isOpen]);

  // Auto-run validation when dialog opens on the validate step
  useEffect(() => {
    if (isOpen && currentStep === "validate" && !validationStatus && !isValidating) {
      handleRunValidation();
    }
  }, [isOpen, currentStep, validationStatus, isValidating, handleRunValidation]);

  const handleNext = useCallback((): void => {
    setError(null);
    switch (currentStep) {
      case "validate":
        if (canProceedFromValidate) setCurrentStep("diff");
        break;
      case "diff":
        if (canProceedFromDiff) setCurrentStep("summary");
        break;
      case "summary":
        if (canProceedFromSummary) setCurrentStep("confirm");
        break;
      default:
        break;
    }
  }, [currentStep, canProceedFromValidate, canProceedFromDiff, canProceedFromSummary]);

  const handleBack = useCallback((): void => {
    setError(null);
    switch (currentStep) {
      case "diff":
        setCurrentStep("validate");
        break;
      case "summary":
        setCurrentStep("diff");
        break;
      case "confirm":
        setCurrentStep("summary");
        break;
      default:
        break;
    }
  }, [currentStep]);

  const [approvalSuccess, setApprovalSuccess] = useState(false);

  const handlePromote = useCallback(async (): Promise<void> => {
    if (!canPromote || !hasChanges || !hasDescription) return;

    setIsPromoting(true);
    setError(null);

    try {
      if (mode === "approval" && onRequestApproval) {
        await onRequestApproval(changeSummary.trim());
        setApprovalSuccess(true);
        setIsPromoting(false);
      } else {
        await onPromote(changeSummary.trim());
        onClose();
      }
    } catch (err) {
      const defaultMsg = mode === "approval" ? "Approval request failed" : "Promotion failed";
      setError(err instanceof Error ? err.message : defaultMsg);
      setIsPromoting(false);
    }
  }, [
    canPromote,
    hasChanges,
    hasDescription,
    changeSummary,
    onPromote,
    onClose,
    mode,
    onRequestApproval,
  ]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isOpen && !isPromoting) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isPromoting, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="promotion-dialog"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {mode === "approval" ? "Request Promotion Approval" : "Promote Configuration"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {verticalKey} / {configType}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPromoting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-[300px]">
          {/* Step 1: Validation */}
          {currentStep === "validate" && (
            <div data-testid="step-validate">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Configuration Validation
                </h3>
                <button
                  type="button"
                  onClick={handleRunValidation}
                  disabled={isValidating}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 disabled:opacity-50 flex items-center gap-1"
                >
                  {isValidating ? "Validating..." : "Re-validate"}
                </button>
              </div>
              <ValidationPanel
                validation={validationStatus}
                isValidating={isValidating}
                error={validationError}
              />
            </div>
          )}

          {/* Step 2: Diff preview */}
          {currentStep === "diff" && (
            <div data-testid="step-diff" className="h-full">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Changes to Promote
              </h3>
              {/* Changed files summary */}
              <div className="mb-4 space-y-2">
                {changedFiles.map((file) => (
                  <ChangedFileBadge key={file.configType} file={file} />
                ))}
                {changedFiles.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No changes to promote</p>
                )}
              </div>
              {/* Diff viewer showing field-level changes */}
              {diffData && diffData.diff.hasChanges && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden h-64">
                  <DiffViewer
                    liveContent={liveContent}
                    draftContent={draftContent}
                    liveVersion={diffData.liveVersion}
                    draftVersion={diffData.draftVersion}
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 3: Change summary */}
          {currentStep === "summary" && (
            <div data-testid="step-summary">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Describe Your Changes
              </h3>
              <label
                htmlFor="change-summary"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Change Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="change-summary"
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                placeholder="Describe the changes being promoted (minimum 10 characters)..."
                maxLength={500}
                rows={4}
                disabled={isPromoting}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500">
                  {changeSummary.length < 10 && changeSummary.length > 0 && (
                    <span className="text-amber-600">
                      {10 - changeSummary.length} more characters required
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500">{changeSummary.length}/500</p>
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {currentStep === "confirm" && (
            <div data-testid="step-confirm">
              {/* Approval success state */}
              {approvalSuccess ? (
                <div data-testid="approval-success">
                  <div className="flex flex-col items-center gap-4 py-6">
                    <CheckIcon className="w-12 h-12 text-green-500" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      Approval Request Submitted
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
                      Approval request submitted. You will be notified when it is reviewed.
                    </p>
                    <a
                      href="/studio/approvals"
                      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      View approval queue
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    {mode === "approval" ? "Confirm Approval Request" : "Confirm Promotion"}
                  </h3>
                  <div className="space-y-4">
                    {/* Summary card */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        {mode === "approval" ? (
                          <>
                            You are about to request approval to promote the draft configuration for{" "}
                            <strong>
                              {verticalKey}/{configType}
                            </strong>
                            . A publisher or admin must approve before promotion.
                          </>
                        ) : (
                          <>
                            You are about to promote the draft configuration for{" "}
                            <strong>
                              {verticalKey}/{configType}
                            </strong>{" "}
                            to live.
                          </>
                        )}
                      </p>
                    </div>

                    {/* Change summary display */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Change Description
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">{changeSummary}</p>
                    </div>

                    {/* Files affected */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Files Affected ({changedFiles.length})
                      </p>
                      <div className="space-y-1">
                        {changedFiles.map((file) => (
                          <div
                            key={file.configType}
                            className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300"
                          >
                            <span>{file.configType}.json</span>
                            <span className="text-xs text-gray-500">
                              {file.liveVersion !== null ? `v${file.liveVersion}` : "New"} {"->"} v
                              {file.draftVersion}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Validation status */}
                    {validationStatus && (
                      <div className="flex items-center gap-2">
                        <CheckIcon className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-700 dark:text-green-300">
                          Validation passed
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            disabled={isPromoting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                       hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>

          <div className="flex items-center gap-3">
            {/* Back button (not on first step, and not after approval success) */}
            {currentStep !== "validate" && !approvalSuccess && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isPromoting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                           hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
              >
                Back
              </button>
            )}

            {/* Next / Promote / Request Approval button */}
            {currentStep === "confirm" ? (
              approvalSuccess ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                             rounded-lg"
                  data-testid="done-button"
                >
                  Done
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePromote}
                  disabled={isPromoting || !canPromote || !hasChanges || !hasDescription}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                             rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid={mode === "approval" ? "request-approval-button" : "promote-button"}
                >
                  {isPromoting && <SpinnerIcon className="w-4 h-4 animate-spin" />}
                  {mode === "approval"
                    ? isPromoting
                      ? "Requesting..."
                      : "Request Approval"
                    : isPromoting
                      ? "Promoting..."
                      : "Promote to Live"}
                </button>
              )
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={
                  (currentStep === "validate" && !canProceedFromValidate) ||
                  (currentStep === "diff" && !canProceedFromDiff) ||
                  (currentStep === "summary" && !canProceedFromSummary)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                           rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-button"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface StepIndicatorProps {
  readonly currentStep: WizardStep;
}

function StepIndicator({ currentStep }: StepIndicatorProps): ReactElement {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
      <div className="flex items-center gap-2">
        {STEPS.map((step, idx) => {
          const isActive = step.key === currentStep;
          const isCompleted = idx < currentIndex;

          return (
            <div key={step.key} className="flex items-center">
              {idx > 0 && (
                <div
                  className={`w-8 h-px mx-2 ${
                    isCompleted ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                  }`}
                />
              )}
              <div className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    isActive
                      ? "bg-blue-600 text-white"
                      : isCompleted
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  {isCompleted ? <CheckSmallIcon className="w-3.5 h-3.5" /> : step.number}
                </div>
                <span
                  className={`text-xs font-medium ${
                    isActive
                      ? "text-blue-700 dark:text-blue-300"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ChangedFileBadgeProps {
  readonly file: PromotionDialogProps["changedFiles"][number];
}

function ChangedFileBadge({ file }: ChangedFileBadgeProps): ReactElement {
  const { configType, diff, liveVersion, draftVersion } = file;

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
      <div className="flex items-center gap-3">
        <FileIcon className="w-5 h-5 text-gray-400" />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{configType}.json</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {liveVersion !== null ? `v${liveVersion}` : "New"} {"->"} v{draftVersion}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {diff.hasChanges ? (
          <>
            {diff.addedFields.length > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400">
                +{diff.addedFields.length}
              </span>
            )}
            {diff.removedFields.length > 0 && (
              <span className="text-xs text-red-600 dark:text-red-400">
                -{diff.removedFields.length}
              </span>
            )}
            {diff.changedFields.length > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                ~{diff.changedFields.length}
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-gray-500">No changes</span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function CloseIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SpinnerIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CheckSmallIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FileIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

export default PromotionDialog;
